import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Spinner } from "react-bootstrap";
import { CheckCircle, XCircle, Plus, Download, RefreshCw, Scissors } from "react-feather";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

function WashoutRegister() {
  const [washoutList, setWashoutList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    BuyContractNo: "",
    SellContractNo: "",
    PartyName: "",
    ItemName: "",
    Qty: "",
    BuyRate: "",
    SellRate: "",
    DiffRate: "",
    TotalDiffAmount: "",
    SettlementDate: new Date().toISOString().split("T")[0],
    Remarks: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === "BuyRate" || name === "SellRate" || name === "Qty") {
        const bRate = parseFloat(name === "BuyRate" ? value : updated.BuyRate) || 0;
        const sRate = parseFloat(name === "SellRate" ? value : updated.SellRate) || 0;
        const qty = parseFloat(name === "Qty" ? value : updated.Qty) || 0;
        const diff = sRate - bRate;
        updated.DiffRate = (diff || 0).toFixed(2);
        updated.TotalDiffAmount = ((diff * qty) || 0).toFixed(2);
      }
      return updated;
    });
  };

  const handleSave = () => {
    if (!formData.PartyName || !formData.Qty || !formData.TotalDiffAmount) {
      toast.error("Please fill Party Name, Qty, and Rates.");
      return;
    }
    setWashoutList(prev => [
      ...prev,
      {
        id: Date.now(),
        ...formData
      }
    ]);
    toast.success("Square-Off / Washout settlement recorded!");
    setShowModal(false);
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(washoutList);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "WashoutSettlements");
    XLSX.writeFile(wb, `Washout_Settlements_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  return (
    <div className="page-content" style={{ padding: "15px" }}>
      <Container fluid>
        <Row className="mb-2 align-items-center">
          <Col xs={12} md={6}>
            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
              <Scissors className="me-2 text-danger" size={22} />
              Square-Off & Washout Settlement Register (सौदा कटाई / वाशआउट रजिस्टर)
            </h5>
            <small className="text-muted">Settling rate differences (Nafa / Nuksan) without physical delivery</small>
          </Col>
          <Col xs={12} md={6} className="text-md-end mt-2 mt-md-0 d-flex justify-content-md-end gap-2">
            <Button size="sm" variant="primary" onClick={() => setShowModal(true)}>
              <Plus size={14} className="me-1" /> + New Washout Settlement
            </Button>
            <Button size="sm" variant="success" onClick={exportToExcel} disabled={washoutList.length === 0}>
              <Download size={14} className="me-1" /> Excel
            </Button>
          </Col>
        </Row>

        <Card className="shadow-sm border-0">
          <Card.Body className="p-0">
            <Table responsive hover striped bordered className="mb-0 text-center align-middle" style={{ fontSize: "0.85rem" }}>
              <thead className="table-dark">
                <tr>
                  <th>Date</th>
                  <th>Buy Cont No</th>
                  <th>Sell Cont No</th>
                  <th>Party Name</th>
                  <th>Item</th>
                  <th>Settled Qty</th>
                  <th>Buy Rate</th>
                  <th>Sell Rate</th>
                  <th>Diff Rate</th>
                  <th>Settlement Amount (₹)</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {washoutList.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-4 text-muted">
                      No washout settlements recorded yet. Click '+ New Washout Settlement' to record rate difference.
                    </td>
                  </tr>
                ) : (
                  washoutList.map((row) => {
                    const diffAmt = parseFloat(row.TotalDiffAmount) || 0;
                    const isProfit = diffAmt >= 0;
                    return (
                      <tr key={row.id}>
                        <td>{row.SettlementDate}</td>
                        <td className="fw-bold text-success">{row.BuyContractNo || "-"}</td>
                        <td className="fw-bold text-primary">{row.SellContractNo || "-"}</td>
                        <td className="text-start ps-2 fw-semibold">{row.PartyName}</td>
                        <td>{row.ItemName}</td>
                        <td className="fw-bold">{row.Qty} Qtl</td>
                        <td>₹ {row.BuyRate}</td>
                        <td>₹ {row.SellRate}</td>
                        <td className={isProfit ? "text-success fw-bold" : "text-danger fw-bold"}>
                          ₹ {row.DiffRate}
                        </td>
                        <td className={`fw-bold fs-6 ${isProfit ? "text-success" : "text-danger"}`}>
                          ₹ {Math.abs(diffAmt).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                        <td>
                          <Badge bg={isProfit ? "success" : "danger"}>
                            {isProfit ? "PROFIT (मुनाफा)" : "LOSS (नुकसान)"}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Modal for Recording New Washout */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
          <Modal.Header closeButton className="bg-light">
            <Modal.Title className="fs-6 fw-bold">
              <Scissors size={18} className="me-2 text-danger" />
              Record Square-Off / Washout Settlement
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-2">
              <Col xs={12} sm={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Settlement Date</Form.Label>
                  <Form.Control type="date" name="SettlementDate" value={formData.SettlementDate} onChange={handleInputChange} size="sm" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Party / Mill Name</Form.Label>
                  <Form.Control type="text" name="PartyName" placeholder="Enter Party Name" value={formData.PartyName} onChange={handleInputChange} size="sm" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Buy Contract No (if linked)</Form.Label>
                  <Form.Control type="text" name="BuyContractNo" placeholder="PUR-XXXX" value={formData.BuyContractNo} onChange={handleInputChange} size="sm" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Sell Contract No (if linked)</Form.Label>
                  <Form.Control type="text" name="SellContractNo" placeholder="SAL-XXXX" value={formData.SellContractNo} onChange={handleInputChange} size="sm" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Item / Commodity</Form.Label>
                  <Form.Control type="text" name="ItemName" placeholder="Soyabean, Oil, etc." value={formData.ItemName} onChange={handleInputChange} size="sm" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Settled Qty (Qtl)</Form.Label>
                  <Form.Control type="number" name="Qty" placeholder="0" value={formData.Qty} onChange={handleInputChange} size="sm" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Buy Rate (₹)</Form.Label>
                  <Form.Control type="number" name="BuyRate" placeholder="0.00" value={formData.BuyRate} onChange={handleInputChange} size="sm" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Sell Rate (₹)</Form.Label>
                  <Form.Control type="number" name="SellRate" placeholder="0.00" value={formData.SellRate} onChange={handleInputChange} size="sm" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Difference Rate (₹)</Form.Label>
                  <Form.Control type="text" name="DiffRate" value={formData.DiffRate} readOnly size="sm" className="bg-light fw-bold" />
                </Form.Group>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Total Difference Amount (₹)</Form.Label>
                  <Form.Control type="text" name="TotalDiffAmount" value={formData.TotalDiffAmount} readOnly size="sm" className="bg-light fw-bold text-primary" />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-bold">Remarks / Narration</Form.Label>
                  <Form.Control as="textarea" rows={2} name="Remarks" placeholder="Settlement terms, Cheque/NEFT reference, etc." value={formData.Remarks} onChange={handleInputChange} size="sm" />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleSave}>Save Settlement</Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default WashoutRegister;
